import type { SaveDriver, SaveSlot } from './types.ts';

const DATABASE_NAME = 'rsvjelle-v2';
const DATABASE_VERSION = 1;
const STORE_NAME = 'saves';

export class IndexedDbSaveDriver implements SaveDriver {
  private databasePromise: Promise<IDBDatabase> | null = null;

  private database(): Promise<IDBDatabase> {
    if (this.databasePromise) return this.databasePromise;
    this.databasePromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('Could not open IndexedDB'));
    });
    return this.databasePromise;
  }

  private async request<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    const database = await this.database();
    return new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, mode);
      const request = action(transaction.objectStore(STORE_NAME));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
      transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
    });
  }

  async read(slot: SaveSlot): Promise<string | null> {
    const value = await this.request('readonly', (store) => store.get(slot));
    return typeof value === 'string' ? value : null;
  }

  async write(slot: SaveSlot, value: string): Promise<void> {
    await this.request('readwrite', (store) => store.put(value, slot));
  }

  async remove(slot: SaveSlot): Promise<void> {
    await this.request('readwrite', (store) => store.delete(slot));
  }
}

